import { useState } from 'react';
import type { JSX } from 'react';

import type {
  BelegbaresProjekt,
  Belegung,
  BelegungSchreibErgebnis,
  BelegungZelle,
  IsoWeek,
  OptimisticLockConflict,
  WeekGrid,
} from '@asas/domain';

import { klassifiziereFehler } from '../daten/fehler';
import type { FehlerBefund } from '../daten/fehler';
import { entitaetsSchluessel, geteilterSerialisierer } from '../daten/serialisierung';
import type { Schreibwarteschlange } from '../daten/warteschlange';
import { trpc } from '../trpc';

import {
  auftragText,
  BEGRUENDUNG_FEHLT,
  begruendungFuer,
  belegungsTitel,
  eingabeText,
  feldAktion,
  geladenesFenster,
  konfliktText,
  UNGUELTIGE_STUNDEN,
  verschiebeZiele,
  wocheGesperrt,
} from './planung';
import type { Planungsauftrag } from './planung';
import { wochenkopf } from './wochenraster';

/** Die Warteschlange der Planung: versionierte Aufträge, Antwort des Servers oder `null` (Löschen). */
export type Planungswarteschlange = Schreibwarteschlange<
  Planungsauftrag,
  BelegungSchreibErgebnis | null
>;

export interface ZellePlanenProps {
  readonly name: string;
  readonly zelle: BelegungZelle;
  readonly raster: WeekGrid;
  readonly laufendeWoche: IsoWeek;
  /** `resource_allocation:edit_past` (AK 7): abgeschlossene Wochen anbieten. */
  readonly nachtragen: boolean;
  /** Während das Raster neu lädt: nichts ändern (apps/web/CLAUDE.md, „Ansichten"). */
  readonly gesperrt: boolean;
  readonly warteschlange: Planungswarteschlange;
  /** Nach dem Speichern: Raster und Zelle neu laden, Überlast der Wochen melden (AK 1, AK 4). */
  readonly onGespeichert: (wochen: readonly IsoWeek[]) => void;
}

/** Eine Rückfrage des Servers (AK 6): erst nach Bestätigung erneut senden. */
type Rueckfrage =
  | { readonly art: 'auftrag'; readonly auftrag: Planungsauftrag; readonly schwelle: number }
  | { readonly art: 'anlage'; readonly anlage: Anlage; readonly schwelle: number };

interface Anlage {
  readonly projectId: string;
  readonly workPackageId: string | null;
  readonly planHours: string;
  readonly begruendung?: string;
}

function fehlerText(befund: FehlerBefund): string {
  return befund.felder.length > 0
    ? befund.felder.map((feld) => feld.message).join(' ')
    : befund.meldung;
}

/**
 * Die bearbeitbaren Belegungen EINER Zelle (AC-403): Stunden je Belegung,
 * gespeichert beim Verlassen des Felds (AK 1), Verschieben in eine andere Woche
 * des Fensters (AK 2), Anlegen auf einem beauftragten oder Akquise-Projekt (H3).
 *
 * Versionierte Aufträge gehen über die Schreibwarteschlange (AC-409): bei
 * Verbindungsverlust warten sie und gehen mit derselben Version erneut ab.
 * Solange gesendet wird, ist die Eingabe gesperrt — sonst käme ein zweiter Wert
 * mit derselben Version als Konflikt gegen den eigenen ersten zurück.
 *
 * Rückfrage (AK 6), Konflikt (AC-409) und abgeschlossene Wochen (AK 7) stellt
 * die Komponente dar; die Entscheidung trifft der Mensch.
 */
export function ZellePlanen({
  name,
  zelle,
  raster,
  laufendeWoche,
  nachtragen,
  gesperrt,
  warteschlange,
  onGespeichert,
}: ZellePlanenProps): JSX.Element {
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [rueckfrage, setRueckfrage] = useState<Rueckfrage | null>(null);
  const [konflikt, setKonflikt] = useState<{
    readonly auftrag: Planungsauftrag;
    readonly konflikt: OptimisticLockConflict;
  } | null>(null);
  const [begruendung, setBegruendung] = useState('');
  /**
   * Baut die Stundenfelder neu auf, ohne dass sich eine Version geändert hat —
   * nach „Abbrechen“ steht wieder der gespeicherte Wert da (Review S1).
   */
  const [feldStand, setFeldStand] = useState(0);
  const [ziele, setZiele] = useState<Readonly<Record<string, string>>>({});
  const [projekte, setProjekte] = useState<readonly BelegbaresProjekt[] | null>(null);
  const [anlage, setAnlage] = useState({ projectId: '', workPackageId: '', planHours: '' });

  const fenster = geladenesFenster(raster);
  const woche: IsoWeek = zelle.woche;
  const titel = `${name}, ${wochenkopf(woche, undefined)}`;
  const zuGesperrt = wocheGesperrt(woche, laufendeWoche, nachtragen);
  const aus = gesperrt || sendet || rueckfrage !== null || konflikt !== null;

  function wochenDes(auftrag: Planungsauftrag): IsoWeek[] {
    return auftrag.art === 'verschieben' ? [woche, auftrag.nachWoche] : [woche];
  }

  async function senden(auftrag: Planungsauftrag): Promise<void> {
    setSendet(true);
    setFehler(null);
    setStatus('Wird gespeichert …');

    const ausgang = await warteschlange.absenden(
      entitaetsSchluessel('allocation_weeks', auftrag.id),
      auftrag,
    );

    setSendet(false);
    setStatus('');

    switch (ausgang.art) {
      case 'gespeichert':
        if (ausgang.ergebnis?.ergebnis === 'bestaetigung_noetig') {
          setRueckfrage({ art: 'auftrag', auftrag, schwelle: ausgang.ergebnis.schwelleStunden });
          return;
        }
        // Review N3: eine Begründung gilt genau einem Nachtrag.
        setBegruendung('');
        onGespeichert(wochenDes(auftrag));
        return;
      case 'bereits_gespeichert':
        setBegruendung('');
        onGespeichert(wochenDes(auftrag));
        return;
      case 'konflikt':
        setKonflikt({ auftrag, konflikt: ausgang.konflikt });
        return;
      case 'wartet':
        setStatus(
          'Keine Verbindung — die Eingabe wird gesendet, sobald die Verbindung zurück ist.',
        );
        return;
      case 'fehler':
        setFehler(fehlerText(ausgang.befund));
        return;
      case 'ueberholt':
        return;
    }
  }

  /** Mit Begründung, falls eine abgeschlossene Woche berührt ist (AK 7); sonst nicht senden. */
  function mitBegruendung(wochen: readonly IsoWeek[]): { readonly begruendung?: string } | null {
    const angabe = begruendungFuer(wochen, laufendeWoche, begruendung);

    if (angabe === 'fehlt') {
      setFehler(BEGRUENDUNG_FEHLT);
      return null;
    }
    return angabe;
  }

  function feldVerlassen(belegung: Belegung, feld: HTMLInputElement): void {
    const aktion = feldAktion(feld.value, belegung.planHours);

    if (aktion.art === 'unveraendert') return;
    if (aktion.art === 'ungueltig') {
      // AK 5: abgewiesen, der Ausgangswert bleibt stehen.
      feld.value = eingabeText(belegung.planHours);
      setFehler(UNGUELTIGE_STUNDEN);
      return;
    }

    const angabe = mitBegruendung([woche]);
    if (angabe === null) {
      feld.value = eingabeText(belegung.planHours);
      return;
    }

    const basis = { id: belegung.id, version: belegung.version, fenster, ...angabe };
    void senden(
      aktion.art === 'loeschen'
        ? { art: 'loeschen', ...basis }
        : { art: 'aendern', ...basis, planHours: aktion.stunden },
    );
  }

  function verschieben(belegung: Belegung): void {
    const wahl = ziele[belegung.id];
    const ziel = raster.wochen.find(
      (kandidat) => `${String(kandidat.isoYear)}-${String(kandidat.isoWeek)}` === wahl,
    );
    if (ziel === undefined) return;

    const angabe = mitBegruendung([woche, ziel]);
    if (angabe === null) return;

    void senden({
      art: 'verschieben',
      id: belegung.id,
      version: belegung.version,
      nachWoche: ziel,
      fenster,
      ...angabe,
    });
  }

  async function projekteLaden(): Promise<void> {
    setFehler(null);
    try {
      setProjekte(await trpc.belegungen.projekte.query());
    } catch (error) {
      setFehler(fehlerText(klassifiziereFehler(error, { schreibzugriff: false })));
    }
  }

  async function anlegen(neu: Anlage, stundenBestaetigt: boolean): Promise<void> {
    setSendet(true);
    setFehler(null);

    try {
      const ergebnis = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('employee', zelle.employeeId),
        () =>
          trpc.belegungen.anlegen.mutate({
            employeeId: zelle.employeeId,
            woche,
            fenster,
            ...neu,
            ...(stundenBestaetigt ? { stundenBestaetigt } : {}),
          }),
      );

      if (ergebnis.ergebnis === 'bestaetigung_noetig') {
        setRueckfrage({ art: 'anlage', anlage: neu, schwelle: ergebnis.schwelleStunden });
        return;
      }
      setAnlage({ projectId: '', workPackageId: '', planHours: '' });
      setBegruendung('');
      onGespeichert([woche]);
    } catch (error) {
      setFehler(fehlerText(klassifiziereFehler(error, { schreibzugriff: true })));
    } finally {
      setSendet(false);
    }
  }

  function anlegenAbsenden(): void {
    const aktion = feldAktion(anlage.planHours, '0');

    if (aktion.art !== 'aendern' || anlage.projectId === '') {
      setFehler(anlage.projectId === '' ? 'Bitte ein Projekt wählen.' : UNGUELTIGE_STUNDEN);
      return;
    }

    const angabe = mitBegruendung([woche]);
    if (angabe === null) return;

    void anlegen(
      {
        projectId: anlage.projectId,
        workPackageId: anlage.workPackageId === '' ? null : anlage.workPackageId,
        planHours: aktion.stunden,
        ...angabe,
      },
      false,
    );
  }

  function bestaetigen(): void {
    if (rueckfrage === null) return;
    setRueckfrage(null);

    if (rueckfrage.art === 'anlage') {
      void anlegen(rueckfrage.anlage, true);
    } else if (rueckfrage.auftrag.art === 'aendern') {
      void senden({ ...rueckfrage.auftrag, stundenBestaetigt: true });
    }
  }

  function konfliktEntscheiden(eigeneUebernehmen: boolean): void {
    if (konflikt === null) return;
    setKonflikt(null);

    if (eigeneUebernehmen) {
      // Der Mensch hat beide Werte gesehen und entschieden: mit der neuen Version erneut.
      void senden({ ...konflikt.auftrag, version: konflikt.konflikt.actualVersion });
    } else {
      warteschlange.verwerfen(entitaetsSchluessel('allocation_weeks', konflikt.auftrag.id));
      onGespeichert([]);
    }
  }

  const gewaehltesProjekt = projekte?.find((projekt) => projekt.projectId === anlage.projectId);

  return (
    <section aria-label="Belegungen bearbeiten">
      <h3>{titel}</h3>
      {fehler === null ? null : <p role="alert">{fehler}</p>}
      <p role="status">{status}</p>

      {zuGesperrt ? (
        <p>Abgeschlossene Woche — Änderungen nur mit erweiterter Berechtigung.</p>
      ) : null}

      {zelle.belegungen.length === 0 ? <p>Keine eingeplanten Stunden.</p> : null}
      <ul>
        {zelle.belegungen.map((belegung) => {
          const geborgen = warteschlange.geborgen(
            entitaetsSchluessel('allocation_weeks', belegung.id),
          );

          return (
            <li key={`${belegung.id}:${String(belegung.version)}:${String(feldStand)}`}>
              <label>
                {belegungsTitel(belegung)}{' '}
                <input
                  type="text"
                  inputMode="decimal"
                  size={6}
                  defaultValue={eingabeText(belegung.planHours)}
                  disabled={aus || zuGesperrt}
                  onBlur={(event) => {
                    feldVerlassen(belegung, event.currentTarget);
                  }}
                />{' '}
                h
              </label>
              {zuGesperrt ? null : (
                <>
                  {' '}
                  <label>
                    nach{' '}
                    <select
                      value={ziele[belegung.id] ?? ''}
                      disabled={aus}
                      onChange={(event) => {
                        setZiele((alt) => ({ ...alt, [belegung.id]: event.target.value }));
                      }}
                    >
                      <option value="">Woche wählen</option>
                      {verschiebeZiele(raster, woche, laufendeWoche, nachtragen).map((ziel) => (
                        <option
                          key={`${String(ziel.isoYear)}-${String(ziel.isoWeek)}`}
                          value={`${String(ziel.isoYear)}-${String(ziel.isoWeek)}`}
                        >
                          {wochenkopf(ziel, undefined)}
                        </option>
                      ))}
                    </select>
                  </label>{' '}
                  <button
                    type="button"
                    disabled={aus || (ziele[belegung.id] ?? '') === ''}
                    onClick={() => {
                      verschieben(belegung);
                    }}
                  >
                    Verschieben
                  </button>
                </>
              )}
              {belegung.nachtraege.length === 0 ? null : (
                <ul aria-label="Nachträge">
                  {belegung.nachtraege.map((nachtrag) => (
                    <li key={`${nachtrag.erfasstAm}:${nachtrag.aktion}`}>
                      Nachtrag ({nachtrag.aktion}): {nachtrag.begruendung}
                    </li>
                  ))}
                </ul>
              )}
              {geborgen === null ? null : (
                <p>
                  Nicht gesendete Eingabe: {auftragText(geborgen)}{' '}
                  <button
                    type="button"
                    disabled={aus}
                    onClick={() => {
                      void senden(geborgen);
                    }}
                  >
                    Jetzt senden
                  </button>{' '}
                  <button
                    type="button"
                    disabled={aus}
                    onClick={() => {
                      warteschlange.verwerfen(entitaetsSchluessel('allocation_weeks', belegung.id));
                      onGespeichert([]);
                    }}
                  >
                    Verwerfen
                  </button>
                </p>
              )}
            </li>
          );
        })}
      </ul>

      {nachtragen ? (
        <p>
          <label>
            Begründung (Pflicht für abgeschlossene Wochen){' '}
            <input
              type="text"
              value={begruendung}
              disabled={aus}
              maxLength={1000}
              onChange={(event) => {
                setBegruendung(event.target.value);
              }}
            />
          </label>
        </p>
      ) : null}

      {rueckfrage === null ? null : (
        <div role="alertdialog" aria-label="Bestätigung">
          <p>
            Mehr als {String(rueckfrage.schwelle)} Stunden in einer Woche — ist der Wert richtig?
          </p>
          <button type="button" onClick={bestaetigen}>
            Ja, speichern
          </button>{' '}
          <button
            type="button"
            onClick={() => {
              setRueckfrage(null);
              setFeldStand((stand) => stand + 1);
            }}
          >
            Abbrechen
          </button>
        </div>
      )}

      {konflikt === null ? null : (
        <div role="alertdialog" aria-label="Konflikt">
          <p>Jemand hat diese Belegung inzwischen geändert.</p>
          <p>{konfliktText(konflikt.konflikt)}</p>
          <button
            type="button"
            onClick={() => {
              konfliktEntscheiden(true);
            }}
          >
            Meine Eingabe übernehmen
          </button>{' '}
          <button
            type="button"
            onClick={() => {
              konfliktEntscheiden(false);
            }}
          >
            Gespeicherten Wert behalten
          </button>
        </div>
      )}

      {zuGesperrt ? null : projekte === null ? (
        <p>
          <button
            type="button"
            disabled={aus}
            onClick={() => {
              void projekteLaden();
            }}
          >
            Belegung hinzufügen
          </button>
        </p>
      ) : (
        <fieldset disabled={aus}>
          <legend>Belegung hinzufügen</legend>
          <label>
            Projekt{' '}
            <select
              value={anlage.projectId}
              onChange={(event) => {
                setAnlage({ ...anlage, projectId: event.target.value, workPackageId: '' });
              }}
            >
              <option value="">Bitte wählen</option>
              {projekte.map((projekt) => (
                <option key={projekt.projectId} value={projekt.projectId}>
                  {projekt.projectLabel}
                  {projekt.status === 'akquise' ? ' (Akquise)' : ''}
                </option>
              ))}
            </select>
          </label>{' '}
          <label>
            Arbeitspaket{' '}
            <select
              value={anlage.workPackageId}
              onChange={(event) => {
                setAnlage({ ...anlage, workPackageId: event.target.value });
              }}
            >
              <option value="">ohne</option>
              {(gewaehltesProjekt?.arbeitspakete ?? []).map((paket) => (
                <option key={paket.id} value={paket.id}>
                  {paket.label}
                </option>
              ))}
            </select>
          </label>{' '}
          <label>
            Stunden{' '}
            <input
              type="text"
              inputMode="decimal"
              size={6}
              value={anlage.planHours}
              onChange={(event) => {
                setAnlage({ ...anlage, planHours: event.target.value });
              }}
            />
          </label>{' '}
          <button type="button" onClick={anlegenAbsenden}>
            Anlegen
          </button>
        </fieldset>
      )}
    </section>
  );
}
